/**
 * Storage adapters that fall back safely when browser APIs are unavailable.
 */
import { safeDocument } from "./utils";

export type StorageAdapter = {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
};

export type CookieOptions = {
  maxAgeSeconds?: number;
  sameSite?: "Lax" | "Strict" | "None";
  secure?: boolean;
};

export type CookieAdapter = {
  get: (key: string) => string | undefined;
  set: (key: string, value: string, options?: CookieOptions) => void;
  remove: (key: string) => void;
};

export const memoryStorage: StorageAdapter = (() => {
  const store = new Map<string, string>();
  return {
    get: (key) => (store.has(key) ? store.get(key)! : null),
    set: (key, value) => {
      store.set(key, value);
    },
    remove: (key) => {
      store.delete(key);
    },
  };
})();

export function createLocalStorageAdapter(): StorageAdapter {
  return {
    get: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {
        // ignore
      }
    },
    remove: (key) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

export function createCookieAdapter(): CookieAdapter {
  return {
    get: (key) => {
      const doc = safeDocument();
      if (!doc) return undefined;
      const match = doc.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${key}=`));
      if (!match) return undefined;
      return decodeURIComponent(match.split("=")[1] ?? "");
    },
    set: (key, value, options = {}) => {
      const doc = safeDocument();
      if (!doc) return;
      const maxAge = options.maxAgeSeconds;
      const sameSite = options.sameSite ?? "Lax";
      const secure =
        options.secure ??
        (typeof location !== "undefined" && location.protocol === "https:");
      const parts = [
        `${key}=${encodeURIComponent(value)}`,
        "path=/",
        `samesite=${sameSite}`,
      ];
      if (typeof maxAge === "number") {
        parts.push(`max-age=${maxAge}`);
      }
      if (secure) {
        parts.push("secure");
      }
      doc.cookie = parts.join(";");
    },
    remove: (key) => {
      const doc = safeDocument();
      if (!doc) return;
      doc.cookie = `${key}=; path=/; max-age=0`;
    },
  };
}

export function createStorageAdapters() {
  const local = createLocalStorageAdapter();
  const cookies = createCookieAdapter();
  return { local, cookies };
}
